/** Git, remotely: the branch map the app draws, the integration queue, and the pull requests panel. */

import { BranchesRight, CircleExclamation, CodePullRequest, Layers, Pencil } from '@gravity-ui/icons';

import { Button, Empty, ListGroup, ListRow, Stat, StatusMark } from '../../components/ui.tsx';
import { RowMenu } from '../../components/ui/RowMenu.tsx';
import { timeAgo } from '../../hooks.ts';
import type { GitStatus, PullRequestsState, Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

export function GitView({ w }: { w: Workbench }) {
  const git = w.useView<GitStatus | null>('git.status', undefined, [], 30_000);
  const prs = w.useView<PullRequestsState>('pullRequests.list', { filter: 'open' }, [], 60_000);
  const g = git.data ?? undefined;
  const branches = g?.branches ?? [];
  const kindOrder = ['base', 'integration', 'member', 'task', 'solo', 'other'];
  const open = prs.data?.pullRequests ?? [];
  const toReview = open.filter((p) => p.needsMyReview).length;
  const dirty = branches.filter((b) => b.dirty).length;
  return (
    <RemoteView>
      <div className="art-stats">
        <Stat tone="rose" icon={<Layers />} label="Branches" value={g ? branches.length : undefined} detail={g ? `${g.worktrees?.length ?? 0} worktrees` : ''} />
        <Stat tone="amber" icon={<Pencil />} label="Uncommitted" value={g ? dirty : undefined} detail={g?.dirtyBase ? `including ${g.base ?? 'the base'}` : 'branches with changes'} />
        <Stat tone="blue" icon={<BranchesRight />} label="To land" value={g?.integration?.exists ? g.integration.pending : g ? 0 : undefined} detail={g?.integration?.exists ? `on ${g.integration.branch}` : 'no integration branch'} />
        <Stat tone="teal" icon={toReview ? <CircleExclamation /> : <CodePullRequest />} label="Pull requests" value={prs.data ? open.length : undefined} detail={toReview ? `${toReview} waiting for your review` : 'open'} needsYou={toReview > 0} />
      </div>
      <ListGroup
        title="Branches"
        count={branches.length || undefined}
        description={g ? `${g.base ?? 'main'} is the base${g.integration?.exists ? `; integration ${g.integration.branch} has ${g.integration.pending} to land` : ''}.` : undefined}
        actions={
          g?.integration?.exists && g.integration.pending > 0 ? (
            <Button size="sm" variant="primary" onPress={() => void w.act('Land integration', () => w.call('git.land', {}), git.reload)}>
              Land {g.integration.pending}
            </Button>
          ) : undefined
        }
        empty={branches.length ? undefined : <Empty tone="rose" icon={<BranchesRight />} compact title="No branch information" body="The workbench reports its branch map once the root is a git repository." />}
      >
        {[...branches]
          .sort((a, b) => kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind))
          .map((b) => (
            <ListRow
              key={b.name}
              title={<span className="mono">{b.name}</span>}
              description={<span title={b.subject}>{[b.kind, b.owner, typeof b.ahead === 'number' ? `${b.ahead} ahead, ${b.behind ?? 0} behind` : undefined, b.unpushed ? `${b.unpushed} unpushed` : undefined, b.lastCommitAt ? timeAgo(new Date(b.lastCommitAt).toISOString()) : undefined, b.subject].filter(Boolean).join(' · ')}</span>}
              status={b.dirty ? <StatusMark state="medium" label="Uncommitted" /> : undefined}
            />
          ))}
      </ListGroup>
      <ListGroup
        title="Pull requests"
        count={prs.data?.pullRequests.length || undefined}
        description={prs.data?.source ? `${prs.data.source.label}${prs.data.source.signedIn ? '' : ' · not signed in'}` : prs.data?.note}
        empty={prs.data?.pullRequests.length ? undefined : <Empty tone="rose" icon={<CodePullRequest />} compact title="No open pull requests" body={prs.data?.note ?? (w.connected ? 'Pull requests on the root’s remote show here once the workbench is signed in to it.' : 'The workbench is not connected.')} />}
      >
        {prs.data?.pullRequests.map((p) => (
          <ListRow
            key={`${p.provider}-${p.id}`}
            title={
              <a href={p.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                #{p.id} {p.title}
              </a>
            }
            description={`${p.state === 'draft' ? 'Draft · ' : ''}${p.author.name} · ${p.source} → ${p.target}${p.updatedAt ? ` · ${timeAgo(p.updatedAt)}` : ''}`}
            status={p.needsMyReview ? <StatusMark state="medium" label="Needs your review" /> : undefined}
            compactControl
            control={
              p.state !== 'merged' && p.state !== 'closed' ? (
                <RowMenu
                  label={`More for #${p.id}`}
                  items={[{ id: 'approve', label: 'Approve' }, p.state === 'open' && { id: 'merge', label: 'Squash and merge' }]}
                  onAction={(k) =>
                    void (k === 'approve'
                      ? w.act('Approve', () => w.call('pullRequests.act', { provider: p.provider, id: p.id, action: { kind: 'approve' } }), prs.reload)
                      : w.act('Merge', () => w.call('pullRequests.act', { provider: p.provider, id: p.id, action: { kind: 'merge', method: 'squash', deleteBranch: true } }), prs.reload))
                  }
                />
              ) : undefined
            }
          />
        ))}
      </ListGroup>
    </RemoteView>
  );
}
